/**
 * §Separators — thousands grouping for money/number DISPLAY across the app.
 *
 * The stored/wire value never carries separators: inputs strip them back out on change
 * (`stripThousands`) so drafts, autosave payloads and conditions keep seeing the raw digits they
 * always have. Grouping is en-US comma style, matching every existing formatter in the repo
 * (payment/finance calculators, function views, i-Score tables).
 *
 * `formatThousands` groups only values that are WHOLLY numeric — a mixed string (a national id
 * with a stray space, free text, an ISO date) passes through untouched. The fractional part is
 * preserved exactly as typed (never rounded, trailing dot kept) so typing "1234." mid-entry
 * renders "1,234." instead of snapping back.
 */
const NUMERIC_TEXT = /^-?\d+(\.\d*)?$/;

export const formatThousands = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '';
	const text = String(value);
	if (!NUMERIC_TEXT.test(text)) return text;
	const [whole, fraction] = text.split('.');
	const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
	return fraction === undefined ? grouped : `${grouped}.${fraction}`;
};

/** The inverse for storage: drop the grouping commas, nothing else. */
export const stripThousands = (text: string): string => text.replace(/,/g, '');

/** Is this (post-strip) something a numeric field may hold mid-entry? `allowNegative` for plain numbers. */
export const isNumericEntry = (text: string, allowNegative = false): boolean =>
	text === '' || (allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/).test(text);
